import { Component, lazy, Suspense, useMemo, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { containModuleFailure } from "@/lib/module-load-recovery";
import LayeredEditLoading from "./layered-edit-loading";
import type LayeredEditMode from "./layered-edit-mode";
import type { ComponentProps } from "react";

type Props = ComponentProps<typeof LayeredEditMode>;
export function loadLayeredEditor() {
  return import("./layered-edit-mode").catch(error => {
    containModuleFailure(error);
    throw error;
  });
}

class EditorBoundary extends Component<{
  children: ReactNode; onBack: () => void; onRetry: () => void;
}, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error: Error) { containModuleFailure(error); }
  render() {
    if (!this.state.failed) return this.props.children;
    return <div className="space-y-4 py-5" data-testid="layered-editor-load-error">
      <p role="alert" className="text-sm text-destructive">Layered editor could not open. Your selected image has not changed. Try again, or use regular edit.</p>
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={this.props.onBack}>Use regular edit</Button>
        <Button type="button" onClick={this.props.onRetry}>Retry editor</Button>
      </div>
    </div>;
  }
}

export default function LayeredEditEntry(props: Props) {
  const [attempt, setAttempt] = useState(0);
  // React.lazy caches rejected promises. A deliberate retry needs a fresh lazy
  // type, not just a remounted boundary. It never retries preparation by itself.
  const Editor = useMemo(() => lazy(loadLayeredEditor), [attempt]);
  return <EditorBoundary key={attempt} onBack={props.onBack} onRetry={() => setAttempt(value => value + 1)}>
    <Suspense fallback={<div><LayeredEditLoading />
      <Button type="button" variant="outline" onClick={props.onBack}>Use regular edit</Button>
    </div>}>
      <Editor {...props} />
    </Suspense>
  </EditorBoundary>;
}
